import { API } from '../lib/api';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  BookOpen,
  Calendar,
  FileText,
  Eye
} from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface Project {
  id: string;
  title: string;
  status: string;
  createdAt: string;
  pages?: { pageNumber: number }[];
  _count?: { pages: number };
}

export const DashboardPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/api/projects?limit=20`);
        
        if (!response.ok) {
          throw new Error('Erreur lors du chargement des projets');
        }
        
        const data = await response.json();
        setProjects(data.projects || []);
      } catch (err) {
        console.error('Erreur fetchProjects:', err);
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement des projets');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const getStatusBadge = (status: string) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-700',
      GENERATING: 'bg-yellow-100 text-yellow-700 animate-pulse',
      READY_FOR_GENERATION: 'bg-blue-100 text-blue-700',
      COMPLETED: 'bg-green-100 text-green-700',
      EXPORTED: 'bg-purple-100 text-purple-700'
    };
    
    const statusLabels = {
      DRAFT: 'Brouillon',
      GENERATING: 'Génération en cours...',
      READY_FOR_GENERATION: 'Prêt',
      COMPLETED: 'Terminé',
      EXPORTED: 'Exporté'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-children ${
        badges[status as keyof typeof badges] || badges.DRAFT
      }`}>
        {statusLabels[status as keyof typeof statusLabels] || status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadingSpinner size="large" text="Chargement de vos projets..." />
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-elegant text-gradient mb-2">
            Mes Projets
          </h1>
          <p className="text-gray-600 font-children">
            Gérez vos livres illustrés
          </p>
        </div>
        
        <Link 
          to="/project/new"
          className="btn-magical inline-flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Nouveau Livre</span>
        </Link>
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Liste des projets */}
      {projects.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-16"
        >
          <BookOpen className="h-24 w-24 text-gray-300 mx-auto mb-6" />
          <h3 className="text-xl font-elegant font-semibold mb-2 text-gray-600">
            Aucun projet pour le moment
          </h3>
          <p className="text-gray-500 font-children mb-8">
            Créez votre premier livre illustré pour commencer
          </p>
          <Link 
            to="/project/new"
            className="btn-magical inline-flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>Créer mon premier livre</span>
          </Link>
        </motion.div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Link 
                to={`/project/${project.id}`}
                className="card hover:scale-105 transition-all duration-200 block"
              >
                {/* Status badge */}
                <div className="flex justify-between items-start mb-4">
                  {getStatusBadge(project.status)}
                  <Eye className="h-4 w-4 text-gray-400" />
                </div>
                
                {/* Titre */}
                <h3 className="text-lg font-elegant font-semibold mb-3 line-clamp-2">
                  {project.title}
                </h3>
                
                {/* Statistiques */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center space-x-1">
                    <FileText className="h-4 w-4" />
                    <span className="font-children">
                      {project._count?.pages || project.pages?.length || 0} pages
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-4 w-4" />
                    <span className="font-children">
                      {formatDate(project.createdAt)}
                    </span>
                  </div>
                </div>
                
                {/* Aperçu du contenu */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 font-children mb-1">
                    Aperçu
                  </div>
                  <div className="h-8 bg-white rounded border-2 border-dashed border-gray-200 flex items-center justify-center">
                    <span className="text-xs text-gray-400 font-children">
                      Illustrations à générer
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};
